<script setup lang="ts">
import { UiAccordion, UiCard, UiIcon, UiText } from "@shopexperts/shared";
</script>

<template>
    <UiCard padding="md">
        <UiAccordion trigger-spread="none">
            <template #trigger>
                <div class="flex items-center gap-2">
                    <UiIcon name="info-circle" class="h-5 w-5 text-se-gray-600" />
                    <UiText as="p" variant="h6" class="font-semibold text-se-dark" text="How screening works" />
                </div>
            </template>

            <div class="grid grid-cols-1 gap-5 pt-4 text-sm text-se-gray-700 lg:grid-cols-2">
                <div class="flex flex-col gap-2">
                    <UiText as="p" variant="h6" class="font-semibold text-se-dark" text="When a rule fires" />
                    <p>
                        A rule holds a list of terms and fires only when <strong>every</strong> term appears in the
                        <strong>same</strong> field. A term in the title plus a term in the description is not a match.
                        Rules are evaluated in id order and the first one that fires wins.
                    </p>
                    <p>
                        Text is stripped of HTML, lowercased and whitespace-collapsed before matching.
                    </p>
                </div>

                <div class="flex flex-col gap-2">
                    <UiText as="p" variant="h6" class="font-semibold text-se-dark" text="What happens on a match" />
                    <p>
                        The submitter's email goes on the ban list, the account behind it is locked and its tokens
                        revoked, the IP is recorded, and the rule's hit counter ticks up. No project, lead, client,
                        organization or invite is created and no email is sent.
                    </p>
                    <p>
                        The endpoint still returns a success-shaped <strong>200</strong>, so the submitter gets no
                        signal about what tripped the rule.
                    </p>
                </div>

                <div class="flex flex-col gap-2">
                    <UiText as="p" variant="h6" class="font-semibold text-se-dark" text="Whose email gets banned" />
                    <p>
                        Anyone can type any address into a public form, so an address is only banned when the submitter
                        has a claim to it: no account behind it, or a request authenticated as that account. Otherwise
                        the submission is rejected and the IP recorded, but nothing is written against that email.
                    </p>
                    <p>A manual ban here always locks the account — a deliberate, reversible admin decision.</p>
                </div>

                <div class="flex flex-col gap-2">
                    <UiText as="p" variant="h6" class="font-semibold text-se-dark" text="Screened endpoints" />
                    <ul class="list-disc space-y-1 pl-5">
                        <li><code>POST /public/draft-leads</code> — title and description</li>
                        <li><code>POST /public/draft-leads/{token}/submit</code> — title and description</li>
                        <li><code>POST /client/leads-v2</code> — title and project brief</li>
                    </ul>
                    <p>
                        Not screened: expert-created client projects, booking-call leads, and the public contact form.
                    </p>
                </div>
            </div>
        </UiAccordion>
    </UiCard>
</template>
